"use client";
import { useState, useEffect, useCallback } from "react";

interface MobileDetectionResult {
  hasDetectedDevice: boolean;
  isMobile: boolean;
  isPortrait: boolean;
  isLandscape: boolean;
  screenWidth: number;
  screenHeight: number;
}

type MobileEnvironmentInput = {
  width: number;
  userAgent?: string;
  maxTouchPoints?: number;
  hasCoarsePointer?: boolean;
  isReactNativeWebView?: boolean;
};

const MOBILE_WIDTH_BREAKPOINT = 1024;

export const isMobileEnvironment = ({
  width,
  userAgent = "",
  maxTouchPoints = 0,
  hasCoarsePointer = false,
  isReactNativeWebView = false,
}: MobileEnvironmentInput): boolean => {
  const isSmallScreen = width < MOBILE_WIDTH_BREAKPOINT;
  const mobileUserAgentPatterns = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
    /Mobile/i,
    /\bwv\b/i,
  ];
  const isMobileUserAgent = mobileUserAgentPatterns.some((pattern) =>
    pattern.test(userAgent)
  );

  return (
    isReactNativeWebView ||
    isMobileUserAgent ||
    isSmallScreen ||
    hasCoarsePointer ||
    (maxTouchPoints > 0 && isSmallScreen)
  );
};

/**
 * Hook to detect mobile devices and orientation
 * Detection criteria:
 * - React Native WebView bridge from nv-zero-mobile
 * - Screen width < 1024px
 * - Mobile/WebView user agent identifiers
 * - Touch/coarse pointer capability
 */
export const useMobileDetection = (): MobileDetectionResult => {
  const [state, setState] = useState<MobileDetectionResult>({
    hasDetectedDevice: false,
    isMobile: false,
    isPortrait: false,
    isLandscape: true,
    screenWidth: typeof window !== "undefined" ? window.innerWidth : 1920,
    screenHeight: typeof window !== "undefined" ? window.innerHeight : 1080,
  });

  const detectMobile = useCallback((): boolean => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return false;
    }

    const hasCoarsePointer =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;
    const isReactNativeWebView = Boolean(
      (window as typeof window & { ReactNativeWebView?: unknown }).ReactNativeWebView
    );

    return isMobileEnvironment({
      width: window.innerWidth,
      userAgent: navigator.userAgent,
      maxTouchPoints: navigator.maxTouchPoints || 0,
      hasCoarsePointer,
      isReactNativeWebView,
    });
  }, []);

  const detectOrientation = useCallback(() => {
    if (typeof window === "undefined") {
      return { isPortrait: false, isLandscape: true };
    }

    const isPortrait = window.innerHeight > window.innerWidth;
    return {
      isPortrait,
      isLandscape: !isPortrait,
    };
  }, []);

  const updateState = useCallback(() => {
    const isMobile = detectMobile();
    const { isPortrait, isLandscape } = detectOrientation();
    const screenWidth = typeof window !== "undefined" ? window.innerWidth : 1920;
    const screenHeight = typeof window !== "undefined" ? window.innerHeight : 1080;

    setState({
      hasDetectedDevice: true,
      isMobile,
      isPortrait,
      isLandscape,
      screenWidth,
      screenHeight,
    });
  }, [detectMobile, detectOrientation]);

  useEffect(() => {
    // Initial detection
    updateState();

    // Listen for resize events (handles orientation changes)
    const handleResize = () => {
      updateState();
    };

    // Listen for orientation change events (more reliable on mobile)
    const handleOrientationChange = () => {
      // Small delay to ensure dimensions are updated
      setTimeout(updateState, 100);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);

    // Also use matchMedia for more reliable orientation detection
    const portraitQuery = window.matchMedia("(orientation: portrait)");
    const handleMediaChange = () => updateState();
    
    if (portraitQuery.addEventListener) {
      portraitQuery.addEventListener("change", handleMediaChange);
    } else {
      // Fallback for older browsers
      portraitQuery.addListener(handleMediaChange);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
      
      if (portraitQuery.removeEventListener) {
        portraitQuery.removeEventListener("change", handleMediaChange);
      } else {
        portraitQuery.removeListener(handleMediaChange);
      }
    };
  }, [updateState]);

  return state;
};

export default useMobileDetection;
